import React, { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import DocumentMeta from 'react-document-meta';
import request from '../../shared/request';
import native from '../../shared/native';
import SupportHeader from '../components/support-header';
import SupportFooter from '../components/support-footer';
import { formatDate, statusText, isClosed, getQueryParam } from './_ticket';

const SupportTicketDetail = (props) => {
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [files, setFiles] = useState([]);
  const [includeLog, setIncludeLog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const id = getQueryParam('id');

  const load = useCallback(async () => {
    if (!id) {
      Toast.fail('工单 id 缺失', 1);
      return;
    }
    setLoading(true);
    const response = await request.call('getUserTicket', { body: { id } });
    setLoading(false);
    if (response.code !== 0) {
      Toast.fail(response.message || '获取工单详情失败', 1);
      return;
    }
    setTicket(response.data.ticket);
    setComments(response.data.comments || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles(prev => [...prev, ...list]);
  };
  const removeFile = (f) => setFiles(prev => prev.filter(x => x !== f));

  const submitReply = async () => {
    if (submitting) return Toast.info('正在提交...', 1);
    const text = replyText.trim();
    if (!text) return Toast.fail('请输入回复内容', 1);
    setSubmitting(true);

    let logUrl = '';
    if (includeLog) {
      try {
        const log = await native.getLog();
        if (log && log.data && log.data.url) logUrl = log.data.url;
      } catch (e) {
        // native bridge unavailable in browser — ignore silently
      }
    }

    const body = { id, body: text, log: logUrl };
    const hasFiles = files.length > 0;
    if (hasFiles) {
      const attachments = {};
      files.forEach(f => { attachments[f.name] = f; });
      body.attachments = attachments;
    }

    const response = await request.call('replyUserTicket', {
      headers: hasFiles ? { 'Content-Type': 'multipart/form-data' } : undefined,
      body,
    });
    setSubmitting(false);

    if (response.code !== 0) {
      return Toast.fail(response.message || '回复失败', 1);
    }
    Toast.success('回复成功', 1);
    setReplyText('');
    setFiles([]);
    setIncludeLog(false);
    load();
  };

  const closed = isClosed(ticket);

  const meta = {
    title: '工单详情',
    description: '',
    meta: { charset: 'utf-8', name: { keywords: '' } },
  };

  return (
    <DocumentMeta {...meta}>
      <SupportHeader />
      <div className="support-container support-container-content">
        <div className="navbar">
          <span className="back-btn" onClick={() => props.history.goBack()}>
            <i className="ri-arrow-left-line" />
          </span>
          <span className="title">工单详情</span>
          <span style={{ width: 24 }} />
        </div>

        <div className="content support-ticket-detail-content">
          <div className={`ticket-detail ${loading ? 'block-loading' : ''}`}>
            <div className="loading">
              <i className="ri-loader-4-line" />
            </div>

            {!loading && ticket && (
              <div>
                <div className="ticket-summary">
                  <div className="ticket-header">
                    <div className="ticket-id">#{ticket.id}</div>
                    <div className={`ticket-status status-${ticket.status}`}>
                      {statusText(ticket.status)}
                    </div>
                  </div>
                  <div className="ticket-subject">{ticket.subject}</div>
                  <div className="ticket-meta">
                    <div className="ticket-date">{formatDate(ticket.created_at)}</div>
                  </div>
                </div>

                <div className="comments">
                  {comments.length === 0 && (
                    <div className="empty-state"><p>暂无回复</p></div>
                  )}
                  {comments.map((c) => (
                    <div key={c.id} className={`comment ${c.is_requester ? 'mine' : 'agent'}`}>
                      <div className="comment-meta">
                        <span className="comment-author">{c.is_requester ? '我' : '客服'}</span>
                        <span className="comment-date">{formatDate(c.created_at)}</span>
                      </div>
                      <div className="comment-body" dangerouslySetInnerHTML={{ __html: c.html_body || c.body }} />
                    </div>
                  ))}
                </div>

                {!closed && (
                  <div className="reply-form">
                    <textarea
                      placeholder="输入回复内容..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={4}
                    />
                    <div className="reply-attachments">
                      <label className="file-picker">
                        <i className="ri-attachment-line" />
                        <span>添加附件</span>
                        <input type="file" multiple onChange={pickFiles} />
                      </label>
                      <label className="log-toggle">
                        <input
                          type="checkbox"
                          checked={includeLog}
                          onChange={(e) => setIncludeLog(e.target.checked)}
                        />
                        <span>附加 App 日志</span>
                      </label>
                    </div>
                    {files.length > 0 && (
                      <div className="file-list">
                        {files.map((f, i) => (
                          <div key={i} className="file-item">
                            <span>{f.name}</span>
                            <i className="ri-close-line" onClick={() => removeFile(f)} />
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={submitReply}
                      disabled={submitting}
                      className={submitting ? 'loading' : ''}
                    >
                      {submitting ? '提交中...' : '发送回复'}
                    </button>
                  </div>
                )}

                {closed && (
                  <div className="ticket-tip">
                    <p>该工单已关闭，如有新问题请提交新工单。</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <SupportFooter />
    </DocumentMeta>
  );
};

export default SupportTicketDetail;
